import { createContext, useContext, useState } from 'react';
import api from '../services/api';
import { calculateSimilarity } from '../utils/auxiliar';

const GameContext = createContext();
const totalLives = 5;

export function GameProvider({ children }) {
  const [gameState, setGameState] = useState({
    rounds: [],
    currentRoundIndex: 0,
    totalScore: 0,
    lives: totalLives,
    isLoading: false,
    error: null,
    gameStarted: false,
    gameEnded: false,
  });

  const startNewGame = async () => {
    setGameState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const language = localStorage.getItem('language') || 'en';
      // Fetch prompts for all rounds
      const promptData = await api.getRandomPrompt(language);
      // Fetch embeddings for ALL completions from all 3 rounds
      const allCompletions = promptData.flatMap((round) => round.suggestions.map((s) => s.suggestion));
      const embeddingsData = await api.fetchEmbeddings(allCompletions);

      // Track position in flat embeddings array
      let embeddingIndex = 0;
      setGameState({
        rounds: promptData.map((round) => ({
          prompt: round.prompt,
          suggestions: round.suggestions.map((s) => {
            const embedding = embeddingsData.embeddings[embeddingIndex];
            embeddingIndex++;
            return {
              ...s,
              embedding,
            };
          }),
          guessedSuggestions: [],
          correctSuggestions: [],
          intermissionSuggestions: [],
          roundScore: 0,
        })),
        currentRoundIndex: 0,
        totalScore: 0,
        lives: totalLives,
        isLoading: false,
        error: null,
        gameStarted: true,
        gameEnded: false,
      });
    } catch (error) {
      console.error('Error in startNewGame:', error);
      setGameState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  //add user autocomplete answer to the array inputs given by user for statistics
  const addNewGuessedSuggestion = (currentRound, roundsArray, autocomplete) => {
    const newInput = [...(currentRound.guessedSuggestions || []), autocomplete];

    roundsArray[gameState.currentRoundIndex] = {
      ...currentRound,
      guessedSuggestions: newInput,
    };

    return roundsArray;
  };

  const guessAutocomplete = async (guessedSuggestion) => {
    const currentRound = getCurrentRound();
    const roundsArray = [...gameState.rounds];
    const updatedRounds = addNewGuessedSuggestion(currentRound, roundsArray, guessedSuggestion);

    let matches = [];
    // 1st: Try to find direct match (case insensitive)
    const matchedSuggestion = currentRound.suggestions.find(
      (s) =>
        !currentRound.correctSuggestions.includes(s.suggestion) &&
        s.suggestion.toLowerCase() === guessedSuggestion.toLowerCase(),
    );
    if (matchedSuggestion) {
      matches.push({
        suggestion: matchedSuggestion, // Full object with points, etc.
        index: currentRound.suggestions.indexOf(matchedSuggestion),
        similarity: 1,
      });
    } else {
      // 2nd: Direct match not found, try embeddings similarity
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 5000),
        );

        // Only embed the user's guess - not the suggestions (already embedded)
        const embeddingPromise = api.fetchEmbeddings([guessedSuggestion]);

        const embeddings = await Promise.race([embeddingPromise, timeoutPromise]);

        const guessEmbedding = embeddings.embeddings[0];
        matches = currentRound.suggestions
          .map((suggestion, index) => {
            const similarity = cosineSimilarity(guessEmbedding, suggestion.embedding);
            return {
              suggestion,
              index,
              similarity,
            };
          })
          .filter(
            ({ suggestion, similarity }) =>
              !currentRound.correctSuggestions.includes(suggestion.suggestion) && similarity >= 0.779,
          );
      } catch (error) {
        console.warn('Embeddings API failed or timed out, falling back to local similarity:', error.message);

        matches = currentRound.suggestions
          .map((suggestion, index) => ({
            suggestion,
            index,
            similarity: calculateSimilarity(guessedSuggestion, suggestion.suggestion),
          }))
          .filter(
            ({ suggestion, similarity }) =>
              !currentRound.correctSuggestions.includes(suggestion.suggestion) && similarity >= 0.57,
          );
      }
    }

    let newLives = gameState.lives;
    let newTotalScore = gameState.totalScore;
    let roundFinished = false;
    let gameFinished = false;
    let matchedIndices = [];

    if (matches.length > 0) {
      const newGuessed = [...currentRound.correctSuggestions, ...matches.map((m) => m.suggestion.suggestion)];
      const roundScore =
        currentRound.roundScore + matches.reduce((sum, m) => sum + (m.suggestion.points || 0), 0);
      newTotalScore += matches.reduce((sum, m) => sum + (m.suggestion.points || 0), 0);
      matchedIndices = matches.map((m) => m.index);

      updatedRounds[gameState.currentRoundIndex] = {
        ...currentRound,
        correctSuggestions: newGuessed,
        roundScore,
      };

      roundFinished = newGuessed.length === updatedRounds[gameState.currentRoundIndex].suggestions.length;

      setGameState((prev) => ({
        ...prev,
        rounds: updatedRounds,
        totalScore: newTotalScore,
      }));

      return {
        status: 1,
        indices: matchedIndices,
        totalPoints: newTotalScore,
        lives: newLives,
        roundFinished: roundFinished,
        gameFinished: false,
      };
    } else {
      newLives = gameState.lives - 1;

      if (newLives === 0) {
        roundFinished = true;
        if (checkHasFinishedRounds()) {
          gameFinished = true;
        }
      }

      setGameState((prev) => ({
        ...prev,
        lives: newLives,
        rounds: updatedRounds,
        gameEnded: gameFinished,
      }));

      return {
        status: 0,
        indices: [], // no matches
        totalPoints: newTotalScore,
        lives: newLives,
        roundFinished: roundFinished,
        gameFinished: gameFinished,
      };
    }
  };

  const checkHasFinishedRounds = () => {
    return !(gameState.currentRoundIndex < gameState.rounds.length - 1);
  };

  const nextRound = () => {
    setGameState((prev) => ({
      ...prev,
      currentRoundIndex: prev.currentRoundIndex + 1,
      lives: totalLives,
    }));
  };

  const getCurrentRound = () => {
    return gameState.rounds[gameState.currentRoundIndex];
  };

  const saveIntermissionSelections = (selections = []) => {
    const currentRound = getCurrentRound();
    const roundsArray = [...gameState.rounds];
    roundsArray[gameState.currentRoundIndex] = {
      ...currentRound,
      intermissionSuggestions: selections,
    };
    setGameState((prev) => ({
      ...prev,
      rounds: roundsArray,
    }));
  };

  const getStatistics = () => {
    const rounds = gameState.rounds;
    const allPrompts = rounds.map((r) => ({
      prompt: r?.prompt,
      guessedSuggestions: r?.guessedSuggestions,
      correctSuggestions: r?.correctSuggestions,
      roundScore: r?.roundScore,
      intermissionSuggestions: r?.intermissionSuggestions,
    }));
    const result = {
      totalScore: gameState.totalScore,
      rounds: allPrompts,
    };
    return result;
  };

  return (
    <GameContext.Provider
      value={{
        gameState,
        startNewGame,
        guessAutocomplete,
        nextRound,
        getCurrentRound,
        saveIntermissionSelections,
        getStatistics,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within GameProvider');
  }
  return context;
}

const cosineSimilarity = (vecA, vecB) => {
  const dotProduct = vecA.reduce((sum, a, i) => sum + a * vecB[i], 0);
  const magnitudeA = Math.sqrt(vecA.reduce((sum, a) => sum + a * a, 0));
  const magnitudeB = Math.sqrt(vecB.reduce((sum, b) => sum + b * b, 0));
  return dotProduct / (magnitudeA * magnitudeB);
};
